/**
 * Subtasks: a checklist inside one task (Boards Redesign extras, card 1,
 * EXTRAS.md §2.1).
 *
 * Stored as `task.subtasks = [{ id, title, done, createdAt }]` IN the task
 * record, like `labels` and `comments` — `decomposeBoard` spreads every task
 * field into the record, so sync, realtime and undo carry it with no store
 * change, and the server only ever sees an encrypted map (no schema change).
 * The array order is the display order. A task without the key (every task
 * written before this, and every 1.0.6 client's new task) reads as `[]`.
 *
 * Subtasks never count toward column / board / overview progress, and ticking
 * the last one does not close the task (Linear and Trello work the same way).
 * Conflicts are last-write-wins on the whole task, the same as labels.
 *
 * ## Limits, and why
 *
 * A task record is ONE sync op, and an op over the per-op cap (UTF-8, Rust's
 * `OUTBOX_ITEM_MAX_BYTES`, 32 000 when these limits were set, 250 000 since
 * 14.10.2026) is never queued — the card silently stops reaching
 * the user's other devices. Comments and the description share that budget, so
 * subtasks get a fixed slice of it:
 *
 * - at most `SUBTASK_MAX_COUNT` (50) items,
 * - each title at most `SUBTASK_TITLE_MAX` (200) characters,
 * - and the whole serialised list at most `SUBTASKS_MAX_BYTES` (16 000 UTF-8
 *   bytes). 50 ASCII titles of 200 characters measure ~15.4 KB, so the byte
 *   cap only bites on long non-Latin titles (200 CJK characters are 600
 *   bytes), where the count cap alone would let the list reach ~32 KB on its
 *   own.
 *
 * Every function here is pure — no React, no store, no records — so the task
 * window, MCP, the in-app AI and git import share one implementation. The
 * editing helpers return the SAME array when nothing changed (a refused add,
 * an unknown id), so a caller can skip the write with `next === prev`.
 */

export const SUBTASK_MAX_COUNT = 50;
export const SUBTASK_TITLE_MAX = 200;
export const SUBTASKS_MAX_BYTES = 16000;

/** UTF-8 length without TextEncoder (absent in some test environments). */
function utf8Bytes(s) {
  let n = 0;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      n += 4; // a surrogate pair is one 4-byte code point
      i += 1;
    } else n += 3;
  }
  return n;
}

/** Serialised size of a subtask list in UTF-8 bytes, as the sync cap counts it. */
export function subtasksBytes(list) {
  return utf8Bytes(JSON.stringify(Array.isArray(list) ? list : []));
}

/** A title as stored: one line, trimmed, at most SUBTASK_TITLE_MAX characters. */
export function cleanSubtaskTitle(title) {
  return String(title ?? '')
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .slice(0, SUBTASK_TITLE_MAX);
}

const defaultId = () => crypto.randomUUID();
const defaultNow = () => new Date().toISOString();

/**
 * A task's subtasks, normalised for reading: always an array; entries that are
 * not objects or have no id are skipped; `done` is a boolean; `title` a string.
 * Never truncates — a list written by a newer client is shown whole.
 */
export function subtasksOf(task) {
  const raw = task?.subtasks;
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const s of raw) {
    if (!s || typeof s !== 'object' || s.id == null || s.id === '') continue;
    const id = String(s.id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      title: typeof s.title === 'string' ? s.title : String(s.title ?? ''),
      done: s.done === true,
      createdAt: typeof s.createdAt === 'string' ? s.createdAt : null,
    });
  }
  return out;
}

/** `{ done, total }` for the "⇄ 2/5" chip and the List view column. */
export function subtaskCounts(taskOrList) {
  const list = Array.isArray(taskOrList) ? taskOrList : subtasksOf(taskOrList);
  let done = 0;
  for (const s of list) if (s?.done === true) done += 1;
  return { done, total: list.length };
}

/** Does a list still have room for one more item with this title? */
export function canAddSubtask(list, title = '') {
  const cur = Array.isArray(list) ? list : [];
  if (cur.length >= SUBTASK_MAX_COUNT) return false;
  const probe = [...cur, { id: '00000000-0000-0000-0000-000000000000', title: cleanSubtaskTitle(title), done: false, createdAt: defaultNow() }];
  return subtasksBytes(probe) <= SUBTASKS_MAX_BYTES;
}

/**
 * Append (or insert at `index`) a new item. An empty title, or a list at its
 * limit, returns `list` unchanged.
 */
export function addSubtask(list, title, { id = defaultId(), now = defaultNow(), index } = {}) {
  const cur = Array.isArray(list) ? list : [];
  const clean = cleanSubtaskTitle(title);
  if (!clean || !canAddSubtask(cur, clean)) return list;
  const item = { id: String(id), title: clean, done: false, createdAt: now };
  const at = Number.isInteger(index) ? Math.max(0, Math.min(index, cur.length)) : cur.length;
  return [...cur.slice(0, at), item, ...cur.slice(at)];
}

/** Rename one item. An empty title removes it (the task window's rule). */
export function renameSubtask(list, id, title) {
  const cur = Array.isArray(list) ? list : [];
  const i = cur.findIndex((s) => s?.id === id);
  if (i === -1) return list;
  const clean = cleanSubtaskTitle(title);
  if (!clean) return removeSubtask(cur, id);
  if (cur[i].title === clean) return list;
  const next = [...cur];
  next[i] = { ...cur[i], title: clean };
  // A rename that would push the list over the byte cap is refused; one that
  // shortens a title is always allowed (it can only help an oversized list).
  const grows = clean.length > String(cur[i].title || '').length;
  return grows && subtasksBytes(next) > SUBTASKS_MAX_BYTES ? list : next;
}

/** Tick / untick one item; `done` omitted flips it. */
export function toggleSubtask(list, id, done) {
  const cur = Array.isArray(list) ? list : [];
  const i = cur.findIndex((s) => s?.id === id);
  if (i === -1) return list;
  const value = done === undefined ? cur[i].done !== true : !!done;
  if ((cur[i].done === true) === value) return list;
  const next = [...cur];
  next[i] = { ...cur[i], done: value };
  return next;
}

/** Move one item to `toIndex` (clamped) — dnd-kit's drop. */
export function moveSubtask(list, id, toIndex) {
  const cur = Array.isArray(list) ? list : [];
  const from = cur.findIndex((s) => s?.id === id);
  if (from === -1 || !Number.isFinite(toIndex)) return list;
  const to = Math.max(0, Math.min(Math.trunc(toIndex), cur.length - 1));
  if (to === from) return list;
  const next = [...cur];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/** Move one item up (-1) or down (+1) — Alt+↑ / Alt+↓. */
export function moveSubtaskBy(list, id, delta) {
  const cur = Array.isArray(list) ? list : [];
  const from = cur.findIndex((s) => s?.id === id);
  if (from === -1) return list;
  return moveSubtask(cur, id, from + Math.sign(delta || 0));
}

/** Remove one item. */
export function removeSubtask(list, id) {
  const cur = Array.isArray(list) ? list : [];
  const next = cur.filter((s) => s?.id !== id);
  return next.length === cur.length ? list : next;
}

/** Does any subtask title contain `query` (already lower-cased, trimmed)? */
export function subtasksMatch(task, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return false;
  return subtasksOf(task).some((s) => s.title.toLowerCase().includes(q));
}

/**
 * An external list (MCP, the in-app AI) → the list to store, or an error.
 *
 * The input REPLACES the task's list: `[{ id?, title, done? }]`. An item with
 * the id of an existing subtask keeps that subtask's `createdAt` (and its
 * `done` when none is sent); an item without an id is new. An id the task
 * does not have is refused rather than minted — a typo would otherwise
 * silently turn into a duplicate.
 *
 * @returns {{ subtasks: object[] } | { error: { field: string, message: string } }}
 */
export function subtasksFromInput(input, existing = [], { newId = defaultId, now = defaultNow() } = {}) {
  const field = 'subtasks';
  if (!Array.isArray(input)) return { error: { field, message: 'subtasks must be an array of { title, done? } objects.' } };
  if (input.length > SUBTASK_MAX_COUNT) {
    return { error: { field, message: `A task can have at most ${SUBTASK_MAX_COUNT} subtasks.` } };
  }
  const byId = new Map(subtasksOf({ subtasks: existing }).map((s) => [s.id, s]));
  const used = new Set();
  const out = [];
  for (let i = 0; i < input.length; i += 1) {
    const item = input[i];
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return { error: { field, message: `subtasks[${i}] must be an object with a title.` } };
    }
    if (typeof item.title !== 'string') {
      return { error: { field, message: `subtasks[${i}].title must be a string.` } };
    }
    if (item.title.trim().length > SUBTASK_TITLE_MAX) {
      return { error: { field, message: `subtasks[${i}].title is longer than ${SUBTASK_TITLE_MAX} characters.` } };
    }
    const title = cleanSubtaskTitle(item.title);
    if (!title) return { error: { field, message: `subtasks[${i}].title must not be empty.` } };
    if (item.done !== undefined && typeof item.done !== 'boolean') {
      return { error: { field, message: `subtasks[${i}].done must be true or false.` } };
    }
    let prev = null;
    if (item.id !== undefined && item.id !== null) {
      const id = String(item.id);
      prev = byId.get(id);
      if (!prev) return { error: { field, message: `subtasks[${i}].id is not a subtask of this task; leave id out to add a new one.` } };
      if (used.has(id)) return { error: { field, message: `subtasks[${i}].id appears twice.` } };
      used.add(id);
    }
    out.push({
      id: prev ? prev.id : String(newId()),
      title,
      done: item.done !== undefined ? item.done : prev ? prev.done : false,
      createdAt: prev?.createdAt || now,
    });
  }
  if (subtasksBytes(out) > SUBTASKS_MAX_BYTES) {
    return { error: { field, message: `These subtasks are too large to sync (over ${SUBTASKS_MAX_BYTES} bytes); shorten the titles or use fewer.` } };
  }
  return { subtasks: out };
}
